import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import RecapModal from "../../../components/Recap/RecapModal";
import Loading from "../../../components/UI/Loading";
import { products } from "../../../dictionaries/products";
import { reportKeyMap } from "../../../dictionaries/reportKeyMap";
import isSubscribed from "../../../helpers/isSubscribed";
import jwt from "../../../helpers/jwt";
import reportsRecapGet, { RecapPayload } from "../../../services/reportsRecapGet";
import { fetchReportSummary as fetchReportSummaryAction } from "../../../store/reportSummary/actions";
import { ReportSummaryState } from "../../../store/reportSummary/types";
import { AppState } from "../../../store/store";
import { UserState } from "../../../store/user/types";
import DashboardAlert from "../components/Alert/DashboardAlert";
import DashboardHeader from "../components/Header/DashboardHeader";
import InvalidUser from "../components/InvalidUser/InvalidUser";
import ModalReason from "../components/ModalReason";
import DashboardRecap from "../components/Recap/DashboardRecap";
import DashboardReport from "../components/Report/DashboardReport";
import DashboardScore from "../components/Score/DashboardScore";
import DashboardTools from "../components/Tools/DashboardTools";

interface Props {
  token: string;
  userId: string;
  userState: UserState;
  reportSummaryState: ReportSummaryState;
  fetchReportSummary(): void;
}

const DashboardContainer = ({ token, userId, userState, reportSummaryState, fetchReportSummary }: Props) => {
  const decodedToken = jwt.decode(token);
  const userIsSubscribed = !!isSubscribed();
  const invalidUser = !!(decodedToken && typeof decodedToken.ignore !== "undefined" ? decodedToken.ignore : false);
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState(false);
  const [recapData, setRecapData] = useState<RecapPayload | null>(null);
  const [showRecap, setShowRecap] = useState(false);

  useEffect(() => {
    const fetchRecap = async () => {
      const cacheKey = `recapData_2025_${userId}`;
      const seenKey = `seenRecap2025_${userId}`;
      const TTL = 6 * 60 * 60 * 1000;

      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.timestamp && parsed.data) {
            const isValid = (Date.now() - parsed.timestamp) < TTL;
            if (isValid) {
              setRecapData(parsed.data);
              if (!localStorage.getItem(seenKey)) {
                setShowRecap(true);
                localStorage.setItem(seenKey, "true");
              }
              return;
            }
          }
        } catch (e) {
          localStorage.removeItem(cacheKey);
        }
      }

      try {
        const response = await reportsRecapGet();
        if (response.success && response.payload) {
          setRecapData(response.payload);
          const cachePayload = {
            timestamp: Date.now(),
            data: response.payload
          };
          localStorage.setItem(cacheKey, JSON.stringify(cachePayload));

          if (!localStorage.getItem(seenKey)) {
            setShowRecap(true);
            localStorage.setItem(seenKey, "true");
          }
        }
      } catch (error) {
        console.error("Failed to fetch recap", error);
      }
    };
    if (userId) {
      // fetchRecap();
    }
  }, [userId]);

  useEffect(() => {
    if (
      (((!userIsSubscribed && !invalidUser) || !invalidUser)) &&
      reportSummaryState.status === "init"
    ) {
      fetchReportSummary();
    }
  }, [userIsSubscribed, invalidUser, reportSummaryState.status, fetchReportSummary]);

  useEffect(() => {
    if (reportSummaryState.status === "loaded") {
      const { payload } = reportSummaryState;
      setAlert(products.some(product => {
        const key = reportKeyMap[product];
        const productsSummary = payload[key] || [];
        return productsSummary.some(({ overdue }) => overdue);
      }))
    }
  }, [reportSummaryState.status])

  useEffect(() => {
    if (userState.status === "loaded") {
      const key = "_eb_reason_question_";
      const { payload } = userState;
      const { reasonQuestionAnswered } = payload;
      const opened = sessionStorage.getItem(key);
      if (!reasonQuestionAnswered && !opened) {
        setOpen(true);
        sessionStorage.setItem(key, "true");
      }
    }
  }, [userState.status])

  if (typeof invalidUser !== "boolean" || typeof userIsSubscribed !== "boolean") {
    return <Loading />;
  }
  if ((!userIsSubscribed && !invalidUser) || !invalidUser) {
    if (reportSummaryState.status === "loading")
      return <Loading />

    if (reportSummaryState.status === "loaded" || reportSummaryState.status === "updating") {
      return (
        <>
          <DashboardHeader />
          {alert && <DashboardAlert />}
          {recapData && (
            <DashboardRecap onViewRecap={() => setShowRecap(true)} />
          )}
          <DashboardScore />
          <DashboardReport />
          <DashboardTools />
          {open && <ModalReason userId={userId} onClose={() => setOpen(false)} />}
          {showRecap && recapData && (
            <RecapModal
              isOpen={showRecap}
              data={recapData}
              onClose={() => setShowRecap(false)}
            />
          )}
        </>
      )
    }
  }
  if (!userIsSubscribed && invalidUser) {
    return (
      <>
        <DashboardScore />
        <DashboardTools />
      </>
    )
  }
  if (invalidUser) {
    return (
      <>
        <InvalidUser />
        <DashboardTools />
      </>
    );
  }
  return <Loading />;
}

const mapStateToProps = (state: AppState) => ({
  userState: state.user,
  userId: state.security.userId,
  token: state.security.token || "",
  reportSummaryState: state.reportSummary,
});

const mapDispatchToProps = {
  fetchReportSummary: fetchReportSummaryAction
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardContainer);